import React, { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { Form, Grid } from "semantic-ui-react";
import EditIconWithLabel from "../../../components/shared/EditIconWithLabel";
import CurrencyDropDown from "../../../components/shared/CurrencyDropDown";
import { GetHotelCurrencies } from "../../../api/services/finance";
import { DatePicker } from "../../../components/shared/DatePicker";
import NumericInput from "../../../components/shared/NumericInput";
import Panel from "../../../components/shared/Panel";
import PriceGroupsDropDown from "../../../components/shared/PriceGroupsDropDown";
import RatesDropDown from "../../../components/shared/RatesDropDown";
import StatisticSourcesDropDown from "../../../components/shared/StatisticSourcesDropDown";
import TextInput from "../../../components/shared/TextInput";
import { useTextResources, useMessageResources } from "../../../language";
import { PriceListContext } from "../context";
import ReservationPoliciesDropDown from "../../../components/shared/ReservationPoliciesDropDown";
import { DerivedCategories, DiscountTypesEnum, RateCategory, RateCommissionTypes } from "../../../components/shared/constants";
import CheckBoxToggle from "../../../components/shared/CheckBoxToggle";
import { Rates } from "../../../api/services/next";
import { onError, onWarning } from '../../../components/shared/messages';
import { HideControlsByRateType } from "../HideControlsByRateType";
import DiscountTypesDropDown from "../../../components/shared/DiscountTypesDropDown";
import { AvailabilitySettings } from "./components/AvailabilitySettings";
import { ReasonCodes } from "./components/ReasonCodes";
import SaleGuideCommentsPopup from "../../../components/SaleGuideCommentsPopup";
import ClassificationCategoriesDropDown from "../../../components/shared/ClassificationCategoriesDropDown";
import { RateCommisionTypeDropDown } from "./components/RateCommisionTypeDropDown";
import { useLoader } from "../../../hooks/useLoader";

export const General = () => {
  const loader = useLoader();
  const text = useTextResources();
  const message = useMessageResources();
  const { state, setState, setDirtyState } = useContext(PriceListContext);
  //const [commissionType, setCommissionType] = useState(RateCommissionTypes.RateCommission);
  const [yieldType, setYieldType] = useState(DiscountTypesEnum.Percent.toString());
  const [showSaleGuideCommentsPopup, setShowSaleGuideCommentsPopup] = useState(false);
  const [baseRates, setBaseRate] = useState();
  const [shouldAddMappings, setShouldAddMappings] = useState(false);

  const isDerived = useMemo(() => {
    return DerivedCategories.includes(state.RateCategoryId)
  }, [state?.RateCategoryId]);

  const yieldProperty = useMemo(() => `YieldByPrice${state?.IsProductPricingIncludesTax ? "AfterTax" : "BeforeTax"}`, [state?.IsProductPricingIncludesTax]);

  const saveRateComments = (rateComments) => {
    changePriceListField('RateSaleGuide', rateComments.rateSaleGuide);
    changePriceListField('ReservationComments', rateComments.reservationComments);
  }
  const changeClubDiscountEligible = (value) => {
    if (value) {
      setShouldAddMappings(value);
    }

    changePriceListField("ClubDiscountEligible", value);
  }

  const changePriceListField = useCallback((fieldName, value) => {
    setState((prev) => ({ ...prev, [fieldName]: value }));
    setDirtyState(prev => ({ ...prev, GeneralTab: true }));
  }, []);

  const setDates = (newDates, fromWhich) => {
    newDates[0] = new Date(newDates[0]);
    newDates[1] = new Date(newDates[1]);
    if (newDates[0] > newDates[1]) {
      let newState = { ...state };
      newState.StartDate = newDates[fromWhich];
      newState.EndDate = newDates[fromWhich];
      setState(newState);
      setDirtyState(prev => ({ ...prev, GeneralTab: true }));
    }
    else {
      changePriceListField("StartDate", newDates[0]);
      changePriceListField("EndDate", newDates[1]);
    }
  }

  useEffect(() => {
    if ((state.YieldByPriceBeforeTax && state.YieldByPriceBeforeTax > 0) || (state.YieldByPriceAfterTax && state.YieldByPriceAfterTax > 0)) {
      setYieldType(DiscountTypesEnum.Amount.toString())
    }
  }, [state?.YieldByPriceBeforeTax, state?.YieldByPriceAfterTax])

  useEffect(() => {
    if (state.YieldByPercent && state.YieldByPercent > 0) {
      setYieldType(DiscountTypesEnum.Percent.toString())
    }
  }, [state?.YieldByPercent]);

  useEffect(() => {
    (async () => {
      if (state?.BaseRateId) {
        try {
          const baseRateResult = await Rates.GetRates({ Id: state.BaseRateId })
          if (baseRateResult) setBaseRate(baseRateResult[0]);
        }
        catch (error) {
          onError(error);
        }
      }
    })()
  }, [])

  const changeBaseRateId = (baseRateId) => {
    changePriceListField("BaseRateId", baseRateId);
    baseRateIdChanged(baseRateId);
  }

  const changeIsActiveField = async (value) => {
    if (!value) {
      const derivedFromRates = await Rates.GetRates({ BaseRateId: state.Id });
      if (derivedFromRates?.length) {
        onWarning(message("DeactivateBaseRate").replace('{0}', derivedFromRates.map(r => r.Name).join(', ')));
        return;
      }
    }

    if (state.RateCategoryId === RateCategory.Derived &&
      state.BaseRateId &&
      value) {
      const baseRate = await Rates.GetRates({ Id: state.BaseRateId, InActive: true });
      if (baseRate.length !== 0 && !baseRate[0].IsActive) {
        onWarning(message("ActivateDerivedRate"));
        return;
      }
    }

    changePriceListField("IsActive", !state.IsActive)
  }

  const baseRateIdChanged = useCallback(async (value) => {
    try {
      loader(true);

      const ratesToDerive = await Rates.GetRates({ BaseRateId: value, GetAncestorRate: true });
      if (ratesToDerive.length === 0) {
        onWarning(message("ErrorRateNotFound").replace("{0}", value).replace("{1}", window.optima.user.Id))
      }
      else {
        const baseRate = ratesToDerive[0];
        const baseRPEs = await Rates.GetRatePricingElements({
          RateId: value, IncludeDummies: true,
          includes: ['IsMappedInChannelManager']
        });
        const derivedRateRPEs = await Rates.GetRatePricingElements({
          IgnoreBaseRate: true, RateId: state.Id, IncludeDummies: true,
          includes: ['IsMappedInChannelManager']
        });
        const derivedRateRPEsExcludingExisiting = derivedRateRPEs.filter(drr => !baseRPEs.map(rpe => rpe.Id).includes(drr.Id))
        changePriceListField("RatePricingElements", [...baseRPEs, ...derivedRateRPEsExcludingExisiting]);

        changePriceListField("StartDate", baseRate.StartDate);
        changePriceListField("EndDate", baseRate.EndDate);

        if (baseRate.CurrencyId) changePriceListField("CurrencyId", baseRate.CurrencyId);

        if (baseRate.YieldByPriceBeforeTax !== undefined) changePriceListField("YieldByPriceBeforeTax", baseRate.YieldByPriceBeforeTax);
        if (baseRate.YieldByPriceAfterTax !== undefined) changePriceListField("YieldByPriceAfterTax", baseRate.YieldByPriceAfterTax);
        if (baseRate.YieldByPercent !== undefined) changePriceListField("YieldByPercent", baseRate.YieldByPercent);

        setBaseRate({ ...baseRate })
      }

      loader(false);

    } catch (error) {
      onError(error);
      loader(false);
    }
  }, [state?.RatePricingElements]);

  const memoizedRatesSpec = useMemo(
    () => ({
      HotelId: state?.HotelId,
      SkipCache: false,
      InheritedRates: false,
      BaseRateId: state?.BaseRateId,
      GetAncestorRate: Boolean(state?.BaseRateId),
    }),
    [state?.HotelId, state?.BaseRateId]
  );

  const isNullOrUndefined = (value) => value === null || value === undefined;
  const RateCommissionFieldControl = () => {
    return <Form.Group >
      <Form.Field control={RateCommisionTypeDropDown}
        clearable
        placeholder={text("Select")}
        width={12}
        value={
          !isNullOrUndefined(state.Commission) ? RateCommissionTypes.RateCommission :
            !isNullOrUndefined(state.CommissionDeduction) ? RateCommissionTypes.CommissionDeduction :
              undefined
        } onChange={(value) => {
          if (value === RateCommissionTypes.RateCommission) {
            changePriceListField("Commission", state.Commission > 0 ? state.Commission : 0);
            changePriceListField("CommissionDeduction", null);
          } else if (value === RateCommissionTypes.CommissionDeduction) {
            changePriceListField("CommissionDeduction", state.CommissionDeduction > 0 ? state.CommissionDeduction : 0);
            changePriceListField("Commission", null);
          } else {
            changePriceListField("Commission", null);
            changePriceListField("CommissionDeduction", null);
          }
        }} />
      <Form.Field
        control={NumericInput}
        selectOnFocus
        disabled={
          isNullOrUndefined(state.Commission) && isNullOrUndefined(state.CommissionDeduction)
        }
        width={4}
        digits={0}
        min={0}
        buttonPlacement="right"
        onChangeComplete={(value) => {
          if (!isNullOrUndefined(state.Commission)) {
            changePriceListField("Commission", value);
          }
          else if (!isNullOrUndefined(state.CommissionDeduction)) {
            changePriceListField("CommissionDeduction", value);
          }
        }}
        value={
          !isNullOrUndefined(state.Commission) ? state.Commission :
            !isNullOrUndefined(state.CommissionDeduction) ? state.CommissionDeduction :
              undefined
        }
      />
    </Form.Group>
  }


    const reservationPolicySpec = useMemo(() => ({
      GetOnlyParentPolicies: true
    }), [])

  return (
    <div>
      <Grid columns={3} stretched>
        <Grid.Row>
          <Grid.Column>
            <Panel header={text("GeneralInformation")} info="PriceListGeneralTabInfo">
              <Form size="small">
                <Form.Field
                  variant="outlined"
                  control={TextInput}
                  label={text("Name")}
                  onChange={(value) => changePriceListField("Name", value)}
                  value={state?.Name}
                  required
                />
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                  {isDerived && <Form.Field
                    label={text("BasedOnRate")}
                    placeholder={text("Select")}
                    control={RatesDropDown}
                    onChange={(value) => {
                      changeBaseRateId(value);
                      changePriceListField("ShowBaseRates", true);
                    }}
                    search
                    required
                    spec={memoizedRatesSpec}
                    value={state?.BaseRateId}
                    disabled={Boolean(!state?.ShowBaseRates && state?.BaseRateId)}
                  />}
                </HideControlsByRateType>
                <Form.Group widths={2}>
                  <DatePicker required label={text("From")} onChange={(value) => setDates([value, state?.EndDate], 0)} clearable={false}
                    filterDate={(date) => {
                      if (state.BaseRateId)
                        return date >= new Date(baseRates.StartDate) && date <= new Date(baseRates.EndDate);
                      else
                        return true;
                    }}
                    value={new Date(state?.StartDate || Date.now())} />
                  <DatePicker required label={text("To")} onChange={(value) => setDates([state?.StartDate, value], 1)} clearable={false}
                    filterDate={(date) => {
                      if (state.BaseRateId)
                        return date >= new Date(baseRates.StartDate) && date <= new Date(baseRates.EndDate);
                      else
                        return true;
                    }}
                    value={new Date(state?.EndDate || Date.now())} />
                </Form.Group>
                <div className="checkbox-wrapper">
                  {text("Active")}
                  <CheckBoxToggle
                    onClick={() => changeIsActiveField(!state.IsActive)}
                    checked={state?.IsActive}
                  />
                </div>
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                  <div className="checkbox-wrapper">
                    {text("Commissionable")}
                    <CheckBoxToggle
                      onClick={() => changePriceListField("Commissionable", !state.Commissionable)}
                      checked={state?.Commissionable}
                    />
                  </div>
                </HideControlsByRateType>
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                  <Form.Field
                    disabled={!state.Commissionable}
                    label={text("RateCommission")}
                    control={RateCommissionFieldControl}
                  />
                </HideControlsByRateType>
              </Form>
            </Panel>
          </Grid.Column>
          <Grid.Column>
            <Panel header={text("AdditionalSettings")} info="PriceListAdditionalSettingsTabInfo">
              <Form size="small">
                <Form.Field
                  label={text("Currency")}
                  control={CurrencyDropDown}
                  api={GetHotelCurrencies}
                  textField="Name"
                  descriptionField="Symbol"
                  value={state?.CurrencyId}
                  required
                  disabled={isDerived || Boolean(state?.Id && state?.savedCurrencyId)}
                  onChange={(value) => changePriceListField("CurrencyId", value)}
                ></Form.Field>
                <div className="checkbox-wrapper">
                  {text("IsProductPricingIncludesTax")}
                  <CheckBoxToggle
                    onChange={(value) => changePriceListField("IsProductPricingIncludesTax", value)}
                    checked={state?.IsProductPricingIncludesTax}
                  />
                </div>
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                  <div className="checkbox-wrapper">
                    {text("AllowSellRoomOnly")}
                    <CheckBoxToggle
                      onChange={(value) => changePriceListField("AllowSellRoomOnly", value)}
                      checked={state?.AllowSellRoomOnly}
                    />
                  </div>
                </HideControlsByRateType>
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                  <div className="checkbox-wrapper">
                    {text("AllowReservationDiscount")}
                    <CheckBoxToggle
                      onChange={(value) => changePriceListField("AllowReservationDiscount", value)}
                      checked={state?.AllowReservationDiscount}
                    />
                  </div>
                </HideControlsByRateType>
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                  <div className="checkbox-wrapper">
                    {text("ConsiderCustomerDiscount")}
                    <CheckBoxToggle
                      onChange={(value) => changePriceListField("ConsiderCustomerDiscount", value)}
                      checked={state?.ConsiderCustomerDiscount}
                    />
                  </div>
                </HideControlsByRateType>
                <HideControlsByRateType rates={[RateCategory.ExtraAddOn, RateCategory.DayUse]}>
                  <Form.Group widths={2}>
                    <Form.Field
                      disabled={isDerived}
                      control={DiscountTypesDropDown}
                      placeholder="Select"
                      clearable
                      label={text("AdditionToYieldBy")}
                      value={yieldType}
                      onChange={(val) => {
                        if (val === yieldType) return;
                        setYieldType(val)
                        if (val == DiscountTypesEnum.Amount)
                          changePriceListField("YieldByPercent", 0);
                        else {
                          changePriceListField("YieldByPriceBeforeTax", 0);
                          changePriceListField("YieldByPriceAfterTax", 0);
                        }
                      }}
                    />
                    <Form.Field
                      control={NumericInput}
                      buttonPlacement="right"
                      label={text(yieldType ? (yieldType == DiscountTypesEnum.Amount ? 'YieldByPrice' : 'YieldByPercent') : 'YieldBy')}
                      disabled={!yieldType || isDerived}
                      min={0}
                      max={yieldType == DiscountTypesEnum.Amount ? undefined : 100}
                      digits={0}
                      value={yieldType == DiscountTypesEnum.Amount ? state[yieldProperty] : state.YieldByPercent}
                      onChange={(value) =>
                        changePriceListField(yieldType == DiscountTypesEnum.Amount ? yieldProperty : "YieldByPercent", parseInt(value))}
                    />
                  </Form.Group>
                </HideControlsByRateType>
              </Form>
            </Panel>
          </Grid.Column>
          <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
            <Grid.Column>
              <AvailabilitySettings />
            </Grid.Column>
          </HideControlsByRateType>
        </Grid.Row>
        <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
          <Grid.Row>
            <Grid.Column>
              <Panel header={text("AdditionalInformation")}>
                <Form size="small">
                  <Form.Group widths={2}>
                    <Form.Field
                      label={text('StatisticSource')}
                      onChange={(value) =>
                        changePriceListField(
                          'StatisticSourceId',
                          value === '' ? undefined : value
                        )
                      }
                      control={StatisticSourcesDropDown}
                      value={state?.StatisticSourceId}
                      clearable
                      search
                    />
                    <Form.Field
                      label={text('ExternalVoucherCode')}
                      onChange={(value) => changePriceListField("ExternalVoucherCode", value)}
                      value={state?.ExternalVoucherCode}
                      control={TextInput}
                      clearable
                      maxCharacters={50}
                    />
                  </Form.Group>
                  <Form.Field
                    label={text("Categories")}
                    onChangeItem={(classifications) =>
                      changePriceListField("ClassificationCategoriesAssignments",
                        classifications?.map(item => ({ ...item, Id: 0, ClassificationId: item.Id, RateId: state.Id }))
                      )}
                    control={ClassificationCategoriesDropDown}
                    multiple clearable search
                    value={state?.ClassificationCategoriesAssignments?.map(item => item.ClassificationId)}
                  />
                  <Form.Group widths={2}>
                    <Form.Field
                      label={text("PriceGroup")}
                      onChange={(value) => changePriceListField("PriceGroupId", value === "" ? undefined : value)}
                      control={PriceGroupsDropDown}
                      value={state?.PriceGroupId}
                      clearable search
                    />
                    <Form.Field
                      label={text("ReservationPolicy")}
                      required
                      onChange={(value) => changePriceListField("PolicyId", value)}
                      control={ReservationPoliciesDropDown}
                      value={state?.PolicyId}
                      spec={reservationPolicySpec}
                    />
                  </Form.Group>
                  <EditIconWithLabel
                    text={text("RateSaleGuideAndReservationComments")}
                    hasData={(!!state.RateSaleGuide || !!state.ReservationComments)}
                    click={() => {
                      setShowSaleGuideCommentsPopup(true);
                    }} />
                  <SaleGuideCommentsPopup
                    open={showSaleGuideCommentsPopup}
                    rateData={{
                      rateSaleGuide: state.RateSaleGuide,
                      reservationComments: state.ReservationComments
                    }}
                    onSave={saveRateComments}
                    onClose={() => setShowSaleGuideCommentsPopup(false)}
                  />
                </Form>
              </Panel>
            </Grid.Column>
            <Grid.Column>
              <Panel header={text("ExternalConnections")}>
                <Form size="small">
                  <HideControlsByRateType rates={[RateCategory.DayUse]}>
                    <div className="checkbox-wrapper">
                      {text("DistributeToExternalChannel")}
                      <CheckBoxToggle
                        checked={state?.IsDistributeToChannelManager}
                        onClick={() =>
                          changePriceListField("IsDistributeToChannelManager", !state.IsDistributeToChannelManager)
                        }
                      />
                    </div>
                    <div className="checkbox-wrapper">
                      {text("AcceptExternalUpdates")}
                      <CheckBoxToggle
                        checked={state?.AcceptExternalUpdates}
                        onClick={() => changePriceListField("AcceptExternalUpdates", !state.AcceptExternalUpdates)}
                      />
                    </div>
                  </HideControlsByRateType>
                  <div className="checkbox-wrapper">
                    {text("AvailableInPMS")}
                    <CheckBoxToggle
                      checked={state?.AvailableInPms}
                      onClick={() => changePriceListField("AvailableInPms", !state.AvailableInPms)}
                    />
                  </div>
                  <HideControlsByRateType rates={[RateCategory.DayUse]}>
                    <div className="checkbox-wrapper">
                      {text("AllowHomeSiteDiscount")}
                      <CheckBoxToggle
                        checked={state?.AllowHomeSiteDiscount}
                        onClick={() => changePriceListField("AllowHomeSiteDiscount", !state.AllowHomeSiteDiscount)}
                      />
                    </div>
                    <div className="checkbox-wrapper">
                      {text("PreferredDisplayOnWebsite")}
                      <CheckBoxToggle
                        checked={state?.PreferredDisplayOnWebsite}
                        onClick={() => changePriceListField("PreferredDisplayOnWebsite", !state.PreferredDisplayOnWebsite)}
                      />
                    </div>
                  </HideControlsByRateType>
                </Form>
              </Panel>
            </Grid.Column>
            <Grid.Column>
              <Panel header={text("ClubSettings")}>
                <Form size="small">
                  <div className="checkbox-wrapper">
                    {text("ClubDiscountEligible")}
                    <CheckBoxToggle
                      checked={state?.ClubDiscountEligible}
                      onClick={() => changeClubDiscountEligible(!state.ClubDiscountEligible)}
                    />
                  </div>
                  <div className="checkbox-wrapper">
                    {text("GainClubPoints")}
                    <CheckBoxToggle
                      checked={state?.GainClubPoints}
                      onClick={() => changePriceListField("GainClubPoints", !state.GainClubPoints)}
                    />
                  </div>
                  <div className="checkbox-wrapper">
                    {text("RedeemClubPoints")}
                    <CheckBoxToggle
                      checked={state?.RedeemClubPoints}
                      onClick={() => changePriceListField("RedeemClubPoints", !state.RedeemClubPoints)}
                    />
                  </div>
                  <ReasonCodes shouldAddMappings={shouldAddMappings} setShouldAddMappings={setShouldAddMappings} />
                </Form>
              </Panel>
            </Grid.Column>
          </Grid.Row>
        </HideControlsByRateType>
      </Grid>
    </div>
  );
};
